import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Alert,
  TextField,
  Switch,
  FormControlLabel,
  Tooltip,
  IconButton,
} from '@mui/material';
import { Button } from 'components/index';
import SelectInput from '../SelectInput';
import { Division, LineItemUnitEnum } from '../glossary/glossary.type';
import SelectDivisionFromGiven from '../glossary/SelectDivisionFromGiven';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { Close, QuestionMark } from '@mui/icons-material';
import NumericFormatInput from '../general/NumericFormatInput';
import { CompLineItem } from './comp.type';

interface Props {
  open: boolean;
  onClose: () => void;
  lineItem: CompLineItem | null;
  updateLineItem: (lineItem: CompLineItem) => void;
  divisions: Division[];
}

const areSame = (a: Partial<CompLineItem>, b: Partial<CompLineItem>) => {
  return a.totalBudget === b.totalBudget && a.quantity === b.quantity && a.unit === b.unit && a.division?.id === b.division?.id;
};

const unitsList = [...Object.values(LineItemUnitEnum).map(unit => ({ value: unit, label: unit })), { label: 'None', value: '' }];

const CompLineItemEditDialog: React.FC<Props> = ({ open, onClose, lineItem: initialLineItem, updateLineItem, divisions }) => {
  const initialOrDefaultValues = {
    totalBudget: initialLineItem?.totalBudget ?? 0,
    quantity: initialLineItem?.quantity ?? 0,
    unit: initialLineItem?.unit ?? null,
    division: initialLineItem?.division ?? null,
    updateDivisionGlobally: initialLineItem?.updateDivisionGlobally ?? true,
  };

  const formik = useFormik({
    initialValues: initialOrDefaultValues,
    enableReinitialize: true,
    validationSchema: yup.object({
      totalBudget: yup.number().required('Required').typeError('Must be a number'),
      quantity: yup.number().min(0, 'Must not be negative').required('Required').typeError('Must be a number'),
    }),
    onSubmit: values => {
      if (!initialLineItem) return;
      console.log('values', values);
      updateLineItem({
        ...initialLineItem,
        ...values,
      });
      formik.resetForm();
      onClose();
    },
  });

  const updateDivision = (newVal: string | null) => {
    if (!initialLineItem) return;
    const newDivision = newVal ? divisions.find(division => division.id === newVal) : null;
    formik.setFieldValue('division', newDivision ?? null);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
        {initialLineItem?.lineItem.description ?? 'Edit Line Item'}
        <IconButton onClick={onClose} sx={{ marginLeft: 'auto' }}>
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {initialLineItem ? (
          <form onSubmit={formik.handleSubmit}>
            <Stack spacing={2} py={2}>
              <TextField
                name='totalBudget'
                label='Total Budget'
                value={formik.values.totalBudget}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.totalBudget && Boolean(formik.errors.totalBudget)}
                helperText={formik.touched.totalBudget && formik.errors.totalBudget}
                variant='standard'
                InputProps={{
                  inputComponent: NumericFormatInput as any,
                }}
              />
              <TextField
                name='quantity'
                label='Quantity'
                value={formik.values.quantity}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.quantity && Boolean(formik.errors.quantity)}
                helperText={formik.touched.quantity && formik.errors.quantity}
                variant='standard'
                InputProps={{
                  inputComponent: NumericFormatInput as any,
                }}
              />
              <SelectInput
                label='Unit'
                selected={formik.values.unit ?? ''}
                setSelected={newVal => {
                  formik.setFieldValue('unit', newVal === '' ? null : newVal);
                }}
                list={unitsList}
              />
              <Stack direction='row' justifyContent='space-between' alignItems={'center'} spacing={2}>
                <SelectDivisionFromGiven
                  label='Division'
                  divisions={divisions}
                  selectedDivision={formik.values.division?.id ?? null}
                  setSelectedDivision={updateDivision}
                />
                <Stack direction='row' alignItems='center' spacing={1}>
                  <FormControlLabel
                    control={
                      <Switch
                        name='updateDivisionGlobally'
                        checked={formik.values.updateDivisionGlobally}
                        onChange={e => formik.setFieldValue('updateDivisionGlobally', e.target.checked)}
                      />
                    }
                    label={'Update Globally'}
                    labelPlacement='end'
                  />
                  <Tooltip title='When this toggle is ON, this line item will be moved to the new division in every report and project. When OFF, the change is for current report only!'>
                    <QuestionMark fontSize='small' />
                  </Tooltip>
                </Stack>
              </Stack>
              <Alert severity='warning'>
                You need to click <b>Submit Changes</b> to avoid losing your changes!
              </Alert>
            </Stack>
          </form>
        ) : (
          <Alert severity='error'>No LineItem Selected</Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant='outlined' color='error' onClick={onClose}>
          Cancel
        </Button>
        <Button variant='contained' color='primary' onClick={formik.submitForm} disabled={areSame(formik.values, formik.initialValues)}>
          Save Draft
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CompLineItemEditDialog;
